// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TitleTypography, { TitleTypographyStyles } from "@stanion/App/Components/TitleTypography/TitleTypography";
import XngCarousel from "@stanion/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@stanion/MobiusOverrides/LazyImage";
import { XNGAGE_CUSTOM_WIDGET } from "@stanion/XDL/constants";
import { color, xScreen } from "@stanion/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    itemsList = "itemsList",
    title = "title",
}
interface OwnProps {
    fields: {
        [fields.itemsList]: {
            fields: {
                title: string;
                image: string;
                description: string;
            };
        }[];
        [fields.title]: string;
    };
}
type Props = OwnProps & WidgetProps;

interface IndustriesStyles {
    container?: GridContainerProps;
    titleTypographyStyles?: TitleTypographyStyles;
    industryGridItem?: GridItemProps;
    imageWrapper?: LazyImageProps;
    item?: {
        title?: TypographyPresentationProps;
        description?: TypographyPresentationProps;
        infoWrapper?: InjectableCss;
    };
    innerBorder?: InjectableCss;
    topLeftCorner?: InjectableCss;
    rightBottomCorner?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
}

const industriesStyles: IndustriesStyles = {
    container: {
        gap: 24,
        css: css`
            margin-bottom: 50px;
        `,
    },
    industryGridItem: {
        width: [12, 12, 6, 6, 3],
        css: css`
            padding-bottom: 38px;
            display: flex;
            flex-direction: column;
            position: relative;
            ${xScreen("lte", "md")} {
                padding: 0;
                max-width: fit-content;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 300px;
            height: 148px;
            clip-path: polygon(10% 0, 100% 0, 100% 80%, 90% 100%, 0 100%, 0 20%);
            margin-bottom: 23px;
            object-fit: cover;
            ${xScreen("lte", "md")} {
                width: 358px;
                height: 173px;
            }
            ${xScreen("lte", "sm")} {
                width: 317px;
                height: 154px;
            }
            img {
                width: 100%;
                height: 100%;
            }
        `,
    },
    item: {
        title: {
            variant: "h3",
            weight: "bold",
            css: css`
                margin: 0;
                padding: 0;
            `,
        },
        description: {
            css: css`
                & > p {
                    font-size: 14px;
                    margin: 0;
                    padding: 0;
                }
            `,
        },
        infoWrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                row-gap: 12px;
                ${xScreen("lte", "md")} {
                    text-align: center;
                }
            `,
        },
    },
    innerBorder: {
        css: css`
            position: absolute;
            background-color: transparent;
            z-index: 10;
            width: 288px;
            height: 140px;
            top: 19px;
            left: 21px;
            clip-path: polygon(10% 0, 100% 0, 100% 80%, 90% 100%, 0 100%, 0 20%);
            border: 2px solid ${color("secondary")};
            ${xScreen("lte", "md")} {
                width: 351px;
                height: 166px;
                top: 3px;
                left: auto;
            }
            ${xScreen("lte", "sm")} {
                height: 144px;
                width: 311px;
            }
        `,
    },
    rightBottomCorner: {
        css: css`
            position: absolute;
            bottom: 27px;
            right: -3px;
            border-bottom: 3px solid ${color("secondary")};
            width: 15%;
            transform: rotate(-45deg);
            transform-origin: right;
            ${xScreen("lte", "md")} {
                bottom: 34px;
            }
            ${xScreen("lte", "sm")} {
                bottom: 28px;
            }
        `,
    },
    topLeftCorner: {
        css: css`
            position: absolute;
            top: -3px;
            left: -15px;
            border-bottom: 3px solid ${color("secondary")};
            width: 15%;
            transform: rotate(-43deg);
            transform-origin: right;
            ${xScreen("lte", "md")} {
                top: -6px;
            }
        `,
    },
    carouselSlidesContainer: {
        css: css`
            .swiper-slide {
                max-width: 400px;
                ${xScreen("lte", "md")} {
                    max-width: 390px;
                }
                ${xScreen("lte", "sm")} {
                    max-width: 360px;
                }
                ${xScreen("lte", "xs")} {
                    max-width: 100%;
                }
            }
        `,
    },
    titleTypographyStyles: {
        wrapper: {
            css: css`
                margin-bottom: 32px;
                ${xScreen("lte", "sm")} {
                    margin-bottom: 42px;
                }
            `,
        },
    },
};

const styles = industriesStyles;

const Industries: FC<Props> = ({ fields }) => {
    const { isSmallMobileScreen, isTabletScreen } = useAppSelector(state => ({
        isSmallMobileScreen: (state as CustomAppState).xCommon.isSmallMobileScreen,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const industriesItems = fields.itemsList?.map(itemField => {
        const item = {
            title: itemField.fields.title,
            img: itemField.fields.image,
            description: itemField.fields.description,
        };
        return (
            <GridItem {...styles.industryGridItem} key={item.title}>
                <StyledWrapper
                    css={css`
                        ${xScreen("lte", "md")} {
                            display: flex;
                            width: 100%;
                            justify-content: center;
                            position: relative;
                        }
                    `}
                >
                    <LazyImage src={item.img} altText={item.title} {...styles.imageWrapper}></LazyImage>
                    <StyledWrapper {...styles.innerBorder}>
                        <StyledWrapper className="topLeftCorner" {...styles.topLeftCorner}></StyledWrapper>
                        <StyledWrapper className="rightBottomCorner" {...styles.rightBottomCorner}></StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.item?.infoWrapper}>
                    <Typography {...styles.item?.title}>{item.title}</Typography>
                    <Typography {...styles.item?.description}>{parse(item.description, parserOptions)}</Typography>
                </StyledWrapper>
            </GridItem>
        );
    });

    return (
        <>
            <StyledWrapper {...styles.container} data-test-selector="industries">
                <TitleTypography extendedStyles={styles.titleTypographyStyles} data-test-selector="industriesTitle">
                    {translate(fields.title) || translate("A Single Source Partner")}
                </TitleTypography>

                {isSmallMobileScreen ? (
                    <GridContainer {...styles.carouselSlidesContainer} data-test-selector="slides">
                        <XngCarousel spaceBetween={20} slidesPerView={1} addStyledPaginationBlock={true}>
                            {industriesItems}
                        </XngCarousel>
                    </GridContainer>
                ) : isTabletScreen ? (
                    <GridContainer {...styles.carouselSlidesContainer} data-test-selector="slides">
                        <XngCarousel
                            spaceBetween={22}
                            slidesPerColumn={2}
                            slidesPerGroup={2}
                            slidesPerColumnFill="row"
                            maximumNumOfSlidesPerView={4}
                            xSlidesPerView={true}
                            addStyledPaginationBlock={true}
                            grabCursor={true}
                        >
                            {industriesItems}
                        </XngCarousel>
                    </GridContainer>
                ) : (
                    <GridContainer>{industriesItems}</GridContainer>
                )}
            </StyledWrapper>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: Industries,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Industries We Serve",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};
export default widgetModule;
